import { useEffect, useState } from 'react';

/**
 * Custom hook to detect different types of user sessions:
 * 1. First ever visit: Neither localStorage nor sessionStorage has the key
 * 2. New session: localStorage has the key but sessionStorage doesn't (user closed and reopened browser)
 * 3. Existing session: Both localStorage and sessionStorage have the key
 * 
 * This helps distinguish between completely new users, returning users in a new session,
 * and users continuing their existing session.
 */
const useIsNewSession = (key: string) => {
  const [isNewSession, setIsNewSession] = useState(false);

  const establishSession = () => {
    const isFirstView = !localStorage.getItem(key) && !sessionStorage.getItem(key);
    
    if (!isFirstView) {
      return;
    }

    localStorage.setItem(key, 'true');
    sessionStorage.setItem(key, 'true');
  };

  const checkSession = () => {
    const hasLocalStorage = Boolean(localStorage.getItem(key));
    const hasSessionStorage = Boolean(sessionStorage.getItem(key));

    // New session is when we have a record in localStorage (returning user)
    // but not in sessionStorage (browser was closed and reopened)
    setIsNewSession(hasLocalStorage && !hasSessionStorage);
  };

  useEffect(() => {
    establishSession();
    checkSession();
  }, []);

  return isNewSession;
};

export default useIsNewSession;
